import { useCallback, useEffect, useState } from 'react';
import { addSeconds, format, parseISO } from 'date-fns';
import isEqual from 'lodash/isEqual';

import { useRiderOptions } from '../../hooks/useRiderOptions';
import { useTimezone } from '../../hooks/useTimezone';
import { formatDate } from '../../utils/helpers';

const waypointOnMapDefault = {
  anchor: false,
  location: undefined,
  latLng: '',
  type: '',
  passengerIds: [],
};

export function useRideEditorState(rideDetails?: RideDetails) {
  const [validatedWaypoints, setValidatedWaypoints] = useState<WaypointFormType[]>([]);
  const [waypointsOnMap, setWaypointsOnMap] = useState<WaypointMarker[]>([waypointOnMapDefault, waypointOnMapDefault]);
  const [raWaypointOnMap, setRAWaypointOnMap] = useState<WaypointMarker | null>(null);
  const riderOptions = useRiderOptions(rideDetails);
  const [completeAnchorTime, setCompleteAnchorTime] = useState<Date>();
  const [tripTemplateForSmartPooling, setTripTemplateForSmartPooling] = useState<RideSeriesAttributes>();
  const [routeEstimates, setRouteEstimates] = useState<WaypointMarker[]>([]);
  const { formatInTimezone } = useTimezone();

  const updateWaypointsOnMap = (updatedWaypoints) => {
    if (updatedWaypoints) {
      setWaypointsOnMap(updatedWaypoints);
    }
  };

  const updateRAWaypointOnMap = () => {
    if (validatedWaypoints?.length) {
      const raIndex = validatedWaypoints?.findIndex((item) => item.isRiderAssistant === true);

      const matchedLocation =
        raIndex >= 0
          ? waypointsOnMap.find((item) => item.location?.placeId === validatedWaypoints[raIndex].location?.placeId)
          : null;

      const raWaypoint: WaypointMarker | null =
        raIndex >= 0
          ? {
              anchor: !!validatedWaypoints[raIndex].anchor,
              type: raIndex === 0 ? 'ra_start' : 'ra_end',
              location: {
                placeId: matchedLocation?.location?.placeId || '',
              },
              latLng: matchedLocation?.latLng,
              address: matchedLocation?.address,
              passengerIds: validatedWaypoints[raIndex].passengerIds,
              slug: validatedWaypoints[raIndex].slug,
            }
          : null;

      setRAWaypointOnMap(raWaypoint);
    }
  };

  useEffect(() => {
    updateRAWaypointOnMap();
  }, [validatedWaypoints, waypointsOnMap]);

  const swapWaypointPositionOnMap = (currentIndex, newIndex) => {
    const tempWaypointsOnMap = waypointsOnMap;
    const tempWaypoint = tempWaypointsOnMap[currentIndex];

    tempWaypointsOnMap[currentIndex] = tempWaypointsOnMap[newIndex];
    tempWaypointsOnMap[newIndex] = tempWaypoint;
    updateWaypointsOnMap([...tempWaypointsOnMap]);
  };

  const updateAnchorTime = useCallback((updatedAnchorTime) => {
    setCompleteAnchorTime(updatedAnchorTime);
  }, []);

  const updateAnchorTimeAndMap = useCallback(
    (updatedRideDetails: RideDetails) => {
      if (updatedRideDetails) {
        const anchor = updatedRideDetails.waypoints?.find((waypoint) => waypoint.anchor);
        const raIndex = updatedRideDetails.waypoints?.findIndex((waypoint) => waypoint.riderAssistants?.length > 0);

        const newWaypoints = updatedRideDetails.waypoints?.map((waypoint, index) => ({
          anchor: waypoint.anchor,
          address: waypoint.location.address,
          location: {
            placeId: waypoint.location.placeId,
          },
          latLng: getWaypointLatLng(waypoint.location),
          type:
            index === 0 ||
            (updatedRideDetails.waypoints.slice(-1)[0].anchor && index < updatedRideDetails.waypoints?.length - 1)
              ? 'pick_up'
              : 'drop_off',
          slug: waypoint.slug,
          passengerIds: waypoint.riders.map((rider) => rider.slug),
        }));

        if (raIndex >= 0) {
          newWaypoints[raIndex].type = raIndex === 0 ? 'ra_start' : 'ra_end';
          setRAWaypointOnMap(newWaypoints[raIndex]);
        }

        if (completeAnchorTime?.getTime() !== anchor.estimatedArrivesAt?.getTime()) {
          updateAnchorTime(anchor.estimatedArrivesAt);
        }
        if (!isEqual(waypointsOnMap, newWaypoints)) {
          updateWaypointsOnMap(newWaypoints);
        }
      }
    },
    [completeAnchorTime, waypointsOnMap, updateAnchorTime]
  );

  // this is for updating the tripTemplateAttributes state when the user changes the trip template
  // detached from the rideDetails state because the rideDetails state is not updated when the user changes the trip template during ride creation
  const updateTripTemplateForSmartPooling = useCallback(
    (updatedTripTemplate?: RideSeriesAttributes) => {
      setTripTemplateForSmartPooling(updatedTripTemplate);
    },
    [tripTemplateForSmartPooling]
  );

  const handleEstimatedTime = useCallback(
    (routeLegs) => {
      if (!completeAnchorTime) {
        setRouteEstimates(waypointsOnMap);

        return;
      }

      const isPickupRoute = waypointsOnMap[0].anchor;
      let newWaypointsOnMap: WaypointMarker[] = [isPickupRoute ? waypointsOnMap[0] : waypointsOnMap.slice(-1)[0]];
      // the actual anchor time was already used for the payload for "route/summary" request
      // this is for finding the time by adding/subtracting durationInSeconds from it
      // -> formatInTimezone is needed to show estimates in user Timezone, NOT browser's TZ
      const timeFromAnchorTime = formatInTimezone(completeAnchorTime, 'HH:mm:ss');
      const hasEstimates = routeEstimates.some((waypoint) => waypoint.estimatedArrivesAt !== undefined);
      const hasNewWaypoints = waypointsOnMap.find((wp) => !wp.slug);

      // use calculated arrival estimates for new rides or after editing route for a persisted ride
      if (!rideDetails || hasEstimates || hasNewWaypoints) {
        routeLegs.forEach((routeLeg) => {
          if (isPickupRoute) {
            const durationInSeconds = Math.round(
              routeLegs.slice(0, routeLeg.endLoc).reduce((acc, leg) => acc + leg.duration, 0)
            );

            newWaypointsOnMap = [
              ...newWaypointsOnMap,
              {
                ...waypointsOnMap[routeLeg.endLoc],
                estimatedArrivesAt: estimateTimeFromDuration(timeFromAnchorTime, isPickupRoute, durationInSeconds),
              },
            ];
          } else {
            const durationInSeconds = Math.round(
              routeLegs.slice(routeLeg.startLoc).reduce((acc, leg) => acc + leg.duration, 0)
            );

            newWaypointsOnMap = [
              ...newWaypointsOnMap.slice(0, -1),
              {
                ...waypointsOnMap[routeLeg.startLoc],
                estimatedArrivesAt: estimateTimeFromDuration(timeFromAnchorTime, isPickupRoute, durationInSeconds),
              },
              ...newWaypointsOnMap.slice(-1),
            ];
          }
        });
      } else {
        newWaypointsOnMap = waypointsOnMap.map((waypoint, waypointIndex) => ({
          ...waypoint,
          estimatedArrivesAt: rideDetails?.waypoints?.[waypointIndex]?.estimatedArrivesAt
            ? `~${format(rideDetails.waypoints[waypointIndex].estimatedArrivesAt, 'hh:mm aaa')}`
            : '',
        }));
      }

      setRouteEstimates(newWaypointsOnMap);
    },
    [waypointsOnMap, completeAnchorTime, rideDetails, formatInTimezone, routeEstimates]
  );

  return {
    validatedWaypoints,
    setValidatedWaypoints,
    waypointsOnMap,
    raWaypointOnMap,
    updateWaypointsOnMap,
    updateRAWaypointOnMap,
    updateAnchorTimeAndMap,
    swapWaypointPositionOnMap,
    completeAnchorTime,
    updateAnchorTime,
    getRiderOptions: () => riderOptions,
    handleEstimatedTime,
    routeEstimates,
    updateTripTemplateForSmartPooling,
    tripTemplateForSmartPooling,
  };
}

const estimateTimeFromDuration = (
  timeFromAnchorTime: string,
  isPickupRoute: boolean,
  durationSeconds: number
): string => {
  if (!durationSeconds) {
    return '';
  }
  // addSeconds needs a Date to work with, so we format a new date with the timeFromAnchorTime
  const formattedDateTime = parseISO(`${formatDate(new Date(), 'yyyy-MM-dd')}T${timeFromAnchorTime}`);
  const newDate = isPickupRoute
    ? addSeconds(formattedDateTime, durationSeconds)
    : addSeconds(formattedDateTime, -durationSeconds);
  const formattedTimeEstimation = formatDate(newDate, 'hh:mm aaa');

  return `~${formattedTimeEstimation}`;
};

const getWaypointLatLng = (waypointLocation) => {
  return waypointLocation.preciseLocation
    ? `${waypointLocation.preciseLocation.lat},${waypointLocation.preciseLocation.lng}`
    : `${waypointLocation.lat},${waypointLocation.lng}`;
};
